import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { Leaf, MessageCircle, ShoppingBasket } from "lucide-react";
import { useCart } from "../context/CartContext";

const HERO_IMG = "/images/hero-fruits.jpg";

export const Hero = () => {
  const { t } = useTranslation();
  const { config } = useCart();
  const stats = t("hero.stats", { returnObjects: true });
  const waLink = `https://wa.me/${config.whatsapp_number}?text=${encodeURIComponent(t("wa.greeting"))}`;

  return (
    <section id="top" className="relative bg-brand-yellow overflow-hidden pt-16">
      <div className="grain-overlay" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center py-16 lg:py-28">
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
          className="lg:col-span-7 relative z-10">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-brand-dark text-brand-yellow text-xs font-bold uppercase tracking-wider">
            <Leaf size={14} /> {t("hero.badge")}
          </span>
          <h1 className="font-heading text-brand-dark text-5xl sm:text-6xl lg:text-8xl leading-[0.95] mt-6 uppercase">
            {t("hero.title1")}<br />
            <span className="text-white [text-shadow:2px_2px_0_#1B5E20]">{t("hero.title2")}</span>
          </h1>
          <p className="mt-6 text-brand-brown text-base sm:text-lg max-w-xl leading-relaxed font-semibold">
            {t("hero.subtitle")}
          </p>
          <div className="mt-8 flex flex-col sm:flex-row flex-wrap gap-4">
            <a href="#products" data-testid="hero-cta-shop"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 min-h-[52px] rounded-full bg-brand-orange text-white font-bold text-sm uppercase tracking-wide shadow-lg hover:-translate-y-1 transition-transform">
              <ShoppingBasket size={18} /> {t("hero.cta")}
            </a>
            <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="hero-cta-whatsapp"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 min-h-[52px] rounded-full bg-white text-brand-dark font-bold text-sm uppercase tracking-wide border-2 border-brand-dark hover:-translate-y-1 transition-transform">
              <MessageCircle size={18} /> {t("hero.ctaWa")}
            </a>
          </div>
          <div className="mt-12 flex flex-wrap gap-6 sm:gap-12">
            {stats.map((s, i) => (
              <div key={i} className="min-w-[90px]">
                <div className="font-heading text-4xl sm:text-5xl text-brand-dark">{s.value}</div>
                <div className="text-[11px] sm:text-xs font-bold text-brand-brown uppercase tracking-wide mt-1">{s.label}</div>
              </div>
            ))}
          </div>
        </motion.div>
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.2 }}
          className="lg:col-span-5 relative">
          <div className="relative rounded-3xl overflow-hidden shadow-2xl rotate-2">
            <img src={HERO_IMG} alt="Buah dan sayuran hasil panen Agrodyke" className="w-full h-[320px] sm:h-[400px] lg:h-[460px] object-cover" />
          </div>
          <div className="absolute -bottom-5 start-2 sm:-bottom-6 sm:-start-6 w-24 h-24 sm:w-36 sm:h-36 animate-spin-slow">
            <svg viewBox="0 0 100 100" className="w-full h-full drop-shadow-lg">
              <circle cx="50" cy="50" r="48" fill="#1B5E20" />
              <circle cx="50" cy="50" r="30" fill="#FFDD00" />
              <path id="badgecircle" d="M 50,50 m -38,0 a 38,38 0 1,1 76,0 a 38,38 0 1,1 -76,0" fill="none" />
              <text fill="#FFDD00" fontSize="10" fontWeight="bold" letterSpacing="2">
                <textPath href="#badgecircle">ORGANIK INDONESIA • AGRODYKE • 100% NATURAL •</textPath>
              </text>
              <text x="50" y="55" textAnchor="middle" fill="#1B5E20" fontSize="16" fontWeight="900">100%</text>
            </svg>
          </div>
        </motion.div>
      </div>
      <div className="absolute bottom-0 inset-x-0 h-8 bg-brand-cream" style={{ clipPath: "polygon(0 100%, 100% 100%, 100% 0, 0 100%)" }} />
    </section>
  );
};
